// Declaração Anual de Pagamentos (IR) — por ano-calendário, em nome do responsável financeiro
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Image as ImageIcon, Loader2, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const B = '/financial/declaracao-ir';
const YEARS = (() => { const y = new Date().getFullYear(); return [y, y - 1, y - 2]; })();

export default function DeclaracaoIrDialog({ open, onOpenChange, studentId, studentName }) {
  const [year, setYear] = useState(YEARS[0]);
  const [respId, setRespId] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const erro = (e, fb) => {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : fb);
  };

  const params = useCallback(() => {
    const p = { year };
    if (respId) p.responsible_id = respId; else p.student_id = studentId;
    return p;
  }, [year, respId, studentId]);

  const load = useCallback(async () => {
    if (!open || (!studentId && !respId)) return;
    setBusy(true);
    try {
      const { data: d } = await api.get(`${B}/preview`, { params: { ...params(), student_id: studentId } });
      setData(d);
      if (!respId && d.responsible?.id) setRespId(d.responsible.id);
    } catch (e) { erro(e, 'Não foi possível carregar a declaração.'); }
    finally { setBusy(false); }
  }, [open, studentId, respId, params]);

  useEffect(() => { load(); }, [load]);

  const baixar = async (fmt) => {
    setBusy(true);
    try {
      const r = await api.get(`${B}/declaracao.${fmt}`, { params: params(), responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) { erro(e, 'Não foi possível gerar a declaração.'); }
    finally { setBusy(false); }
  };

  const enviar = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`${B}/share`, { params: params() });
      const url = `${process.env.REACT_APP_BACKEND_URL}${d.pdf_path}`;
      const msg = encodeURIComponent(`${d.text}${url}`);
      window.open(d.phone ? `https://wa.me/${d.phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
      toast.success(d.phone ? `Declaração pronta para ${d.responsible_name || 'o responsável'}.`
        : 'Responsável sem telefone: escolha o contato no WhatsApp.');
    } catch (e) { erro(e, 'Não foi possível preparar o envio.'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto"
        data-testid="declaracao-ir-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" /> Declaração Anual de Pagamentos (IR)
          </DialogTitle>
          <DialogDescription>
            Documento em nome do responsável financeiro, com todos os filhos e o valor dedutível de
            serviços educacionais do ano-calendário{studentName ? ` · ${studentName}` : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5" data-testid="ir-years">
          {YEARS.map((y) => (
            <button key={y} type="button" onClick={() => setYear(y)} data-testid={`ir-year-${y}`}
              className={`text-xs font-semibold rounded-lg border px-3 py-1.5 ${year === y ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}>
              {y}
            </button>
          ))}
        </div>

        {!!data?.guardians?.length && (
          <div>
            <p className="text-xs font-semibold mb-1.5">Responsável da declaração</p>
            <div className="flex flex-wrap gap-1.5" data-testid="ir-guardians">
              {data.guardians.map((g) => (
                <button key={g.id} type="button" onClick={() => setRespId(g.id)} data-testid={`ir-resp-${g.id}`}
                  className={`text-[11.5px] rounded-full border px-2.5 py-1 ${respId === g.id ? 'bg-violet-100 border-violet-400 text-violet-900 font-semibold' : 'bg-white hover:bg-slate-50'}`}>
                  {g.name}{g.is_financial ? ' · financeiro' : ''}{g.cpf ? '' : ' · sem CPF'}
                </button>
              ))}
            </div>
          </div>
        )}

        {data?.warning && (
          <p className="text-[12px] text-amber-700 flex items-center gap-1.5" data-testid="ir-warning">
            <AlertTriangle className="w-4 h-4" /> {data.warning}
          </p>
        )}

        <div className="rounded-xl border bg-slate-50 p-3" data-testid="ir-preview">
          {busy && !data ? (
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
            </p>
          ) : !data?.count ? (
            <p className="text-sm text-slate-500">Nenhum pagamento registrado em {year} para este responsável.</p>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase text-slate-500">
                {data.responsible?.name} · CPF {data.responsible?.cpf || '—'} · ano-calendário {year}
              </p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Box label="Serviços educacionais" value={brl(data.educational_total)} accent />
                <Box label="Outros itens" value={brl(data.other_total)} />
                <Box label="Total no ano" value={brl(data.total)} />
              </div>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                {data.groups.map((g) => (
                  <div key={g.student_id}>
                    <p className="text-[12px] font-bold text-violet-800">
                      {g.name} · {brl(g.edu + g.other)}
                    </p>
                    {g.items.map((i, k) => (
                      <div key={`${g.student_id}-${k}`} className="flex justify-between text-[12px] text-slate-700">
                        <span className="truncate pr-2">{i.date || '—'} · {i.description}
                          {i.educational ? '' : ' (não dedutível)'}</span>
                        <span className="font-medium">{brl(i.amount)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 sm:flex-none">Fechar</Button>
          <Button variant="outline" onClick={() => baixar('jpg')} disabled={busy || !data?.count}
            data-testid="ir-jpeg" className="flex-1 sm:flex-none">
            <ImageIcon className="w-4 h-4 mr-1.5" /> JPEG
          </Button>
          <Button variant="outline" onClick={() => baixar('pdf')} disabled={busy || !data?.count}
            data-testid="ir-pdf" className="flex-1 sm:flex-none border-violet-200 text-violet-700">
            <FileText className="w-4 h-4 mr-1.5" /> Declaração em PDF
          </Button>
          <Button onClick={enviar} disabled={busy || !data?.count} data-testid="ir-send"
            className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700">
            <Send className="w-4 h-4 mr-1.5" /> Enviar no WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Box = ({ label, value, accent }) => (
  <div className={`rounded-lg p-2 ${accent ? 'bg-violet-50 border border-violet-200' : 'bg-white border'}`}>
    <p className="text-[10.5px] text-slate-500">{label}</p>
    <p className={`text-sm font-bold ${accent ? 'text-violet-700' : 'text-slate-800'}`}>{value}</p>
  </div>
);
